import { FC, useMemo, useState } from 'react'
import classNames from 'classnames'
import Button from '@UI/New/Button'
import { IcoRemove } from '@/utils/icons'
import { getCorrectFilePath } from '@/helpers/new/getCorrectFilePath'
import { getDocumentDisplayName } from '@pages/Admin/Requests/requests.utils'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import EndDateWithProcessedCell from '@pages/Admin/Reports/ContractReport/components/EndDateWithProcessedCell/EndDateWithProcessedCell'
import EndDateWithProcessedColumnHeader from '@pages/Admin/Reports/ContractReport/components/EndDateWithProcessedCell/EndDateWithProcessedColumnHeader'
import SigningStateCell from '@pages/Admin/Reports/ContractReport/components/SigningStateCell/SigningStateCell'
import SendForSignatureModal from '@pages/Admin/Reports/ContractReport/components/SendForSignatureModal/SendForSignatureModal'
import {
  formatContractUploadedAt,
  formatSigningType,
  getContractFileName,
  getSignedStatusDetails,
} from '@pages/Admin/Reports/ContractReport/utils/contractSigningState.utils'
import LegacyButton from '@UI/Button'
import parentCn from '../../index.module.css'
import cn from './index.module.css'
import {
  IStudentDocksListProps,
  StudentContractDock,
} from './studentContracts.types'
import {
  buildStudentContractDocks,
  canSendContractForSignature,
  getSendForSignatureTooltip,
} from './studentContracts.utils'
import { usePodpislonReady } from '@pages/Admin/Reports/ContractReport/utils/usePodpislonReady'
import { useStudentContractActions } from './useStudentContractActions'

const StudentDocksList: FC<IStudentDocksListProps> = ({
  student = {},
  onDeleteDock = () => {},
  onContractChanged = () => {},
}) => {
  const { podpislonReady, isCheckingPodpislon } = usePodpislonReady()
  const [sendModalContract, setSendModalContract] =
    useState<StudentContractDock | null>(null)
  const [viewAll, setViewAll] = useState(false)

  const {
    contractLoading,
    handleContractEndDateChange,
    handleContractToggleProcessed,
    handleContractSigningStateChange,
  } = useStudentContractActions(onContractChanged)

  const documentsAndContracts = useMemo(
    () => buildStudentContractDocks(student),
    [student],
  )

  const contractsToShow = viewAll
    ? documentsAndContracts
    : documentsAndContracts.slice(0, 3)

  return (
    <div className={cn.containerStudentDocks}>
      <div className={cn.containerStudentDocksList}>
        <div className={cn.docksListRow}>
          <div className={cn.docksListHeader}>
            <div className={parentCn.textSemiBold}>Дата и время загрузки</div>
            <EndDateWithProcessedColumnHeader
              className={parentCn.textSemiBold}
            />
            <div className={parentCn.textSemiBold}>Название документа</div>
            <div className={parentCn.textSemiBold}>Тип подписания</div>
            <div
              style={{ textAlign: 'center' }}
              className={parentCn.textSemiBold}
            >
              Файл
            </div>
            <div
              style={{ textAlign: 'center' }}
              className={parentCn.textSemiBold}
            >
              На подпись
            </div>
          </div>
          <div className={cn.deleteDockButtonPlaceholder} aria-hidden />
        </div>
        {contractsToShow.map((dock) => {
          const dockKey =
            dock.typeDocument === 'contract'
              ? `contract-${dock.id}`
              : `document-${dock.id ?? dock.idTwo}`

          const canSendForSignature = canSendContractForSignature(
            dock,
            podpislonReady,
            isCheckingPodpislon,
          )
          const sendForSignatureTooltip = getSendForSignatureTooltip(
            dock,
            podpislonReady,
            isCheckingPodpislon,
          )
          const sendForSignatureButton = (
            <Button
              color="primary"
              disabled={!canSendForSignature}
              onClick={() => setSendModalContract(dock)}
            >
              На подпись
            </Button>
          )

          return (
            <div className={cn.docksListRow} key={dockKey}>
              <div className={cn.docksListItem}>
                <div>{formatContractUploadedAt(dock)}</div>
                {dock.typeDocument === 'contract' ? (
                  <EndDateWithProcessedCell
                    date={dock.contract_end_date ?? null}
                    processed={dock.is_processed}
                    dateLoading={
                      contractLoading?.id === dock.id &&
                      contractLoading?.field === 'endDate'
                    }
                    processedLoading={
                      contractLoading?.id === dock.id &&
                      contractLoading?.field === 'processed'
                    }
                    popupPlacement="topLeft"
                    closeOnScroll
                    onDateChange={(date) =>
                      handleContractEndDateChange(dock, date)
                    }
                    onToggleProcessed={() =>
                      handleContractToggleProcessed(dock)
                    }
                  />
                ) : (
                  <div className={cn.docksListEmptyCell}>—</div>
                )}
                <div className={cn.documentNameCell}>
                  {dock.typeDocument === 'document'
                    ? getDocumentDisplayName(dock)
                    : dock.name}
                </div>
                {dock.typeDocument === 'contract' ? (
                  <SigningStateCell
                    fixedSelectWidth
                    signingState={dock.signing_state}
                    signingStateLabel={dock.signing_state_label}
                    signingTypeLabel={formatSigningType(dock.signing_type)}
                    contractStatusHistory={dock.contract_status_history as any}
                    signedDetails={getSignedStatusDetails(dock)}
                    loading={
                      contractLoading?.id === dock.id &&
                      contractLoading?.field === 'signingState'
                    }
                    dropdownPlacement="topLeft"
                    closeOnScroll
                    onChange={(signingState) =>
                      handleContractSigningStateChange(dock, signingState)
                    }
                  />
                ) : (
                  <div className={cn.docksListEmptyCell}>—</div>
                )}
                <a
                  className={classNames(
                    cn.contractFileLink,
                    parentCn.truncateText,
                  )}
                  href={getCorrectFilePath(dock?.file_path ?? dock?.path)}
                  target="_blank"
                  rel="noreferrer"
                  title={getContractFileName(dock?.file_path ?? dock?.path)}
                >
                  {getContractFileName(dock?.file_path ?? dock?.path)}
                </a>
                {dock.typeDocument === 'contract' ? (
                  <div className={cn.sendForSignatureButtonWrapper}>
                    {!canSendForSignature && sendForSignatureTooltip ? (
                      <TooltipWrapper
                        title={sendForSignatureTooltip}
                        style={{ width: '100%', display: 'flex' }}
                      >
                        <span
                          className={classNames(
                            cn.sendForSignatureButtonSlot,
                            cn.sendForSignatureButtonSlotDisabled,
                          )}
                        >
                          {sendForSignatureButton}
                        </span>
                      </TooltipWrapper>
                    ) : (
                      <span className={cn.sendForSignatureButtonSlot}>
                        {sendForSignatureButton}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className={cn.docksListEmptyCell} />
                )}
              </div>
              <div className={cn.deleteDockButtonWrapper}>
                <div
                  className={cn.deleteDockButton}
                  onClick={() => onDeleteDock(dock)}
                >
                  <IcoRemove />
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {documentsAndContracts.length > 3 && (
        <LegacyButton
          newDesign
          name=""
          className={classNames(parentCn.secondaryButton, parentCn.truncateText)}
          action={() => setViewAll((prev) => !prev)}
        >
          {viewAll ? 'скрыть' : 'смотреть еще'}
        </LegacyButton>
      )}
      <SendForSignatureModal
        open={!!sendModalContract}
        contract={
          sendModalContract
            ? {
                id: sendModalContract.id,
                name: sendModalContract.name,
                file_path:
                  sendModalContract.file_path ?? sendModalContract.path,
              }
            : null
        }
        student={{
          id: student?.id,
          name: student?.name,
          phone: student?.phone,
          parents: student?.parents ?? [],
        }}
        onClose={() => setSendModalContract(null)}
        onSuccess={async () => {
          await onContractChanged()
        }}
      />
    </div>
  )
}

export default StudentDocksList
